// / <reference path="js/modules/react-stripe.d.ts" />
import React, { useState, useEffect, useRef } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import InputHint from '../InputHint';
import { GENERIC ,AUTHENTICATION_FAILED } from './constants/stripeValidationMessages';

interface IProps {
    doProcessOrder: Function;
    buildFormDataAndConfirm: Function;
    bookingError: Function;
    isProcessingBooking: boolean;
    updatePageErrors: Function;
    setPlaceholders: Function;
    paymentIntent: string;
    paymentClientSecret: string;
    SCAChallengeRequired: boolean;
}

interface IFieldErrors {
    cardNumberError?: boolean;
    cardExpiryError?: boolean;
    cardCvcError?: boolean;
}

interface IFieldErrorMessage {
    cardNumberErrorMessage?: string;
    cardExpiryErrorMessage?: string;
    cardCvcErrorMessage?: string;
}

const options = {
    iconStyle: 'solid',
    style: {
        base: {
            fontSize: '14px',
            color: '#575757',
            letterSpacing: '0.03rem',
            '::placeholder': {
                color: '#8C8C8C',
            },
        },
        invalid: {
            color: 'red',
        },
    },
};

/**
 * Renders Stripe payment form
 */
const StripeCardElements: React.FC<IProps> = ({
    doProcessOrder,
    bookingError,
    buildFormDataAndConfirm,
    isProcessingBooking,
    setPlaceholders,
    paymentIntent,
    paymentClientSecret,
    SCAChallengeRequired,
    updatePageErrors,
}) => {
    const stripeHook = useStripe();
    const elements = useElements();
    const [fieldError, setFieldError] = useState<IFieldErrors>({
        cardNumberError: false,
        cardExpiryError: false,
        cardCvcError: false,
    });
    const [fieldErrorMessage, setFieldErrorMessage] = useState<IFieldErrorMessage>({
        cardNumberErrorMessage: '',
        cardExpiryErrorMessage: '',
        cardCvcErrorMessage: '',
    });
    const [paymentMethodId, setPaymentMethodId] = useState('');
    const [initialisedStripe, setStripe] = useState(null);
    const [initialisedElements, setElements] = useState(null);
    const paymentMethodIdRef = useRef('');

    useEffect(() => {
        if (SCAChallengeRequired && paymentClientSecret) {
            handleCardAction(paymentClientSecret);
        }
    }, [SCAChallengeRequired]);

    useEffect(() => {
        if (!stripeHook || !elements) {
            // We can't create a PaymentRequest until Stripe.js loads.
            return;
        }
        setStripe(stripeHook);
        setElements(elements);
        paymentMethodIdRef.current = paymentMethodId;
    }, [stripeHook, elements, paymentMethodId]);

    const createPaymentMethod = (event: any) => {
        doProcessOrder(event, (isValid: any) => {
            if (isValid) {
                updateGlobalErrorMessage();
                handStripePaymentMethod();
            }
        });
    };

    const handStripePaymentMethod = () => {
        const cardNumber = initialisedElements.getElement(CardNumberElement);

        initialisedStripe
            .createPaymentMethod({ type: 'card', card: cardNumber })
            .then((result: any) => {
                const {
                    error,
                    paymentMethod: { id, card },
                } = result;
                setPaymentMethodId(id);

                if (error) {
                    updatePageErrors([GENERIC]);
                    bookingError();
                } else {
                    buildFormDataAndConfirm(id);
                }
            })
            .catch((err: any) => {
                updatePageErrors([GENERIC]);
                bookingError();
            });
    };

    const handleCardAction = (clientSecret: string) => {
        initialisedStripe
            .handleCardAction(clientSecret)
            .then((response: any) => {
                if (response.error) {
                    updatePageErrors([AUTHENTICATION_FAILED]);
                    bookingError();
                } else {
                    buildFormDataAndConfirm(paymentMethodId, paymentIntent);
                }
            })
            .catch((err: any) => {
                updatePageErrors([GENERIC]);
                bookingError();
            });
    };

    const handleInputChange = (change: any) => {
        const { error, elementType } = change;

        const errorField = `${elementType}Error`;
        const errorMessage = `${elementType}ErrorMessage`;

        if (error) {
            setFieldError((prevState) => ({ ...prevState, [errorField]: true }));
            setFieldErrorMessage((prevState) => ({ ...prevState, [errorMessage]: error.message }));
        } else {
            setFieldError((prevState) => ({ ...prevState, [errorField]: false }));
            setFieldErrorMessage((prevState) => ({ ...prevState, [errorMessage]: '' }));
        }
    };

    const updateGlobalErrorMessage = () => {
        updatePageErrors([]);
    };

    const { cardPlaceholder, cvvPlaceholder, cvvText } = setPlaceholders();
    const inputWrapperClassName =
        'c-form__field c-form__field--default  c-form__field--hasError c-form__field--payment';
    const labelClassName = 'c-form__label c-form__label--default c-form__field--payment c-form__label--isRequired';
    const inputErrorClassName = 'c-form__error';

    return (
        <div className="card-details flex-row-wrap-top">
            <div className="p-cardNumber">
                <div className={inputWrapperClassName}>
                    <label className={labelClassName}>Card number</label>
                    <CardNumberElement
                        placeholder={cardPlaceholder}
                        options={options}
                        onChange={(event: any) => handleInputChange(event)}
                    />
                    {fieldError.cardNumberError && (
                        <div className={inputErrorClassName}>{fieldErrorMessage.cardNumberErrorMessage}</div>
                    )}
                </div>
            </div>
            <div className="p-expiryDate">
                <div className={inputWrapperClassName}>
                    <label className={labelClassName}>Expiry Date</label>
                    <CardExpiryElement options={options} onChange={(event: any) => handleInputChange(event)} />
                    {fieldError.cardExpiryError && (
                        <div className={inputErrorClassName}>{fieldErrorMessage.cardExpiryErrorMessage}</div>
                    )}
                </div>
            </div>
            <div className="p-cvv">
                <div className={inputWrapperClassName}>
                    <label className={labelClassName}>Cvv</label>
                    <CardCvcElement
                        placeholder={cvvPlaceholder}
                        options={options}
                        onChange={(event: any) => handleInputChange(event)}
                    />
                    <InputHint hintInfo={cvvText} type="stripeElement" />
                    {fieldError.cardCvcError && (
                        <div className={inputErrorClassName}>{fieldErrorMessage.cardCvcErrorMessage}</div>
                    )}
                </div>
            </div>
            <button
                className="p-paymentButton p-paymentButton--elements button-secondary"
                disabled={isProcessingBooking}
                type="submit"
                onClick={(event) => createPaymentMethod(event)}
            >
                Pay now
            </button>
        </div>
    );
};

export default StripeCardElements;